/* eslint-disable */
import { BaseAdapter } from './BaseAdapter'
import { UnifiedTransformer } from '../pipeline/UnifiedTransformer'
import { CSSFilterFallback } from '../fallback/CSSFilterFallback'
import { detectBrowserSupport } from '../utils/browser'
import type {
  BackgroundBlurConfig,
  BackgroundFilterType,
  ColorFilterConfig,
  SupportInfo,
  VideoBackgroundConfig,
  VirtualBackgroundConfig
} from '../core/types'

/**
 * Adapter for the Vonage / OpenTok video platform.
 *
 * Uses a single {@link UnifiedTransformer} that orchestrates background
 * segmentation (native MediaPipe), color filters, and NSFW sampling in
 * one pipeline pass via `@vonage/media-processor` Insertable Streams.
 *
 * Falls back to CSS filters for browsers that lack Insertable Streams.
 *
 * Since all effects live in one transformer, there is **no** slot
 * limitation — background and color filters can be active simultaneously
 * in the pipeline (both affect the remote stream).
 */
export class OpenTokAdapter extends BaseAdapter {
  private publisher: any = null
  private unifiedTransformer: UnifiedTransformer
  private cssFallback: CSSFilterFallback
  private connectorAttached = false
  private mediaProcessor: any = null
  private activeColorConfig: ColorFilterConfig | null = null
  private colorMode: 'pipeline' | 'css' | null = null
  private _opQueue: Promise<void> = Promise.resolve()

  private enqueue<T> (fn: () => Promise<T>): Promise<T> {
    const result = this._opQueue.then(fn)
    this._opQueue = result.then(() => {}, () => {})
    return result
  }

  constructor () {
    super()
    this.unifiedTransformer = new UnifiedTransformer()
    this.cssFallback = new CSSFilterFallback()
  }

  // ─── Lifecycle ────────────────────────────────────────────────────

  async attachToPublisher (publisher: any): Promise<void> {
    this.publisher = publisher
  }

  async detachFromPublisher (): Promise<void> {
    await this.removeColorFilter()

    if (this.publisher && this.connectorAttached) {
      try {
        await this.publisher.clearVideoMediaProcessorConnector()
      } catch (e) {
        // Publisher may already be destroyed
      }
      this.connectorAttached = false
    }
    this.publisher = null
  }

  // ─── Background Filters ──────────────────────────────────────────

  async enableBackgroundBlur (config: BackgroundBlurConfig): Promise<void> {
    return this.enqueue(async () => {
      this.ensurePublisher()
      await this.unifiedTransformer.enableBackground('blur', config)
      await this.ensureConnectorAttached()
    })
  }

  async setVirtualBackground (config: VirtualBackgroundConfig): Promise<void> {
    return this.enqueue(async () => {
      this.ensurePublisher()
      await this.unifiedTransformer.enableBackground('virtual', config)
      await this.ensureConnectorAttached()
    })
  }

  async setVideoBackground (config: VideoBackgroundConfig): Promise<void> {
    return this.enqueue(async () => {
      this.ensurePublisher()
      await this.unifiedTransformer.enableBackground('video', config)
      await this.ensureConnectorAttached()
    })
  }

  async disableBackground (): Promise<void> {
    return this.enqueue(async () => {
      this.unifiedTransformer.disableBackground()
    })
  }

  getActiveBackgroundType (): BackgroundFilterType {
    return this.unifiedTransformer.getBackgroundType()
  }

  // ─── Color Filters ───────────────────────────────────────────────

  async applyColorFilter (config: ColorFilterConfig): Promise<void> {
    return this.enqueue(async () => {
      this.ensurePublisher()
      this.activeColorConfig = config

      const support = this.isSupported()

      if (support.insertableStreams) {
        this.unifiedTransformer.enableColor(config)
        await this.ensureConnectorAttached()
        if (this.colorMode === 'css') {
          this.cssFallback.remove()
        }
        this.colorMode = 'pipeline'
      } else {
        this.applyColorCSS(config)
      }
    })
  }

  async removeColorFilter (): Promise<void> {
    return this.enqueue(async () => {
      if (this.colorMode === 'pipeline') {
        this.unifiedTransformer.disableColor()
      } else if (this.colorMode === 'css') {
        this.cssFallback.remove()
      }

      this.activeColorConfig = null
      this.colorMode = null
    })
  }

  getActiveColorConfig (): ColorFilterConfig | null {
    return this.activeColorConfig ? { ...this.activeColorConfig } : null
  }

  // ─── Utils ────────────────────────────────────────────────────────

  isSupported (): SupportInfo {
    return detectBrowserSupport()
  }

  getName (): string {
    return 'opentok'
  }

  async destroy (): Promise<void> {
    await this.removeColorFilter()
    await this.detachFromPublisher()
    this.unifiedTransformer.destroy()
  }

  /** Expose the unified transformer for NSFW integration. */
  getUnifiedTransformer (): UnifiedTransformer {
    return this.unifiedTransformer
  }

  // ─── Private ──────────────────────────────────────────────────────

  /**
   * Ensure the unified transformer is connected to the publisher via
   * a single `MediaProcessor` + `MediaProcessorConnector`. The
   * connector is created lazily and reused for all filters.
   */
  private async ensureConnectorAttached (): Promise<void> {
    if (this.connectorAttached) return
    if (!this.publisher) return

    const mpModule = await import('@vonage/media-processor')
    const MP = (mpModule as any).MediaProcessor || (mpModule as any).default
    const MPC = (mpModule as any).MediaProcessorConnector

    const mp = new MP()
    await mp.setTransformers([this.unifiedTransformer as any])

    const connector = new MPC(mp)
    await this.publisher.setVideoMediaProcessorConnector(connector)

    this.mediaProcessor = mp
    this.connectorAttached = true
  }

  private applyColorCSS (config: ColorFilterConfig): void {
    const videoEl = this.getPublisherVideoElement()
    if (videoEl) {
      if (this.cssFallback.isActive()) {
        this.cssFallback.update(config)
      } else {
        this.cssFallback.apply(videoEl, config)
      }
    }
    this.colorMode = 'css'
  }

  private getPublisherVideoElement (): HTMLElement | null {
    if (!this.publisher) return null
    const container = this.publisher.element || this.publisher.targetElement
    if (container && container.querySelector) {
      return container.querySelector('video') || container
    }
    return null
  }

  private ensurePublisher (): void {
    if (!this.publisher) {
      throw new Error('No publisher attached. Call attach() first.')
    }
  }
}
